import React, {
  useEffect, memo, useCallback, useRef,
  useMemo,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import isUndefined from 'lodash/isUndefined';
import omitBy from 'lodash/omitBy';
import noop from 'lodash/noop';
import pick from 'lodash/pick';
import { useLogTrackMutation } from '../../../../../store/services/common/logTrack/logTrackApi';
import pubSubInstance from '../../../../../shared/utils/pubSub';
import { setLatestBillingContact } from '../../../Payment/components/PaymentMethods/paymentMethodsSlice';
import { setRenderShippingSuccess } from '../../passPortWidgetsSlice';

const ShippingAddress = () => {
  const dispatch = useDispatch();
  const {
    entities: {
      userInfo: {
        epid,
        shippingInfo: {
          widgetToken,
          shippingItems,
        } = {},
      } = {},
    },
    defaultData: { shippingAddress: { shippingAddressId } = {} } = {},
  } = useSelector(state => state.mainStateData);

  const {
    passportWidgetsJsReady,
    renderShippingSuccess,
  } = useSelector(state => state.passPortWidgets || {});
  const {
    locale,
    widgetNameSpace,
  } = useSelector(state => state.propsData);
  const {
    billingContact = {},
  } = useSelector(state => state.paymentMethods);

  const shippingSectionId = 'passport-widget-shipping-address';
  const [ logTrack ] = useLogTrackMutation();
  const renderShippingAddressResult = useRef(null);
  const verificationTrigger = useRef(null);
  const getAddressTrigger = useRef(null);
  const submitTrigger = useRef(null);
  const triggers = {
    formVerificationTrigger: (trigger) => {
      verificationTrigger.current = trigger;
    },
    getAddressTrigger: (trigger) => {
      getAddressTrigger.current = trigger;
    },
    submitTrigger: (trigger) => {
      submitTrigger.current = trigger;
    },
  };
  const shouldRenderWidget = useMemo(() => epid && widgetToken && passportWidgetsJsReady,
    [ epid, widgetToken, passportWidgetsJsReady ]);

  const destroyWidget = () => {
    if (renderShippingAddressResult.current) {
      renderShippingAddressResult.current.destroy();
    }
  };

  const renderWidget = useCallback(() => {
    const params = {
      locale,
      widgetToken,
      epid,
      defaultSelectId: shippingAddressId,
      triggers,
      shippingItems,
      show: noop,
      hide: noop,
      initCallback: ({ status }) => {
        dispatch(setRenderShippingSuccess(status === 'SUCCESS'));
      },
    };
    const prop = omitBy(params, isUndefined);

    destroyWidget(); // For props are from async api, So it should rerender the widget
    renderShippingAddressResult.current = window.APWWidget.renderer.renderShippingAddressWidget(
      document.getElementById(shippingSectionId), prop);
  }, [ locale, widgetToken, epid, shippingItems, shippingAddressId ]);

  useEffect(() => {
    if (shouldRenderWidget) {
      renderWidget();
    }
    return () => {
      destroyWidget();
    };
  }, [ shouldRenderWidget, renderWidget ]);

  const handleValidate = useCallback(() => {
    return new Promise((resolve) => {
      if (!shouldRenderWidget || !renderShippingSuccess) {
        resolve(true);
      } else {
        verificationTrigger.current().then(({ status }) => {
          if (status === 'SUCCESS') {
            resolve(true);
          } else {
            resolve(false);
          }
        });
      }
    });
  }, [ verificationTrigger.current, shouldRenderWidget, renderShippingSuccess ]);

  const handleSubmit = useCallback(() => {
    return new Promise((resolve) => {
      if (!shouldRenderWidget) {
        resolve({ status: true });
      }
      if (renderShippingSuccess) {
        submitTrigger.current().then(({ status, values, message }) => {
          const shippingAddress = pick(values, [ 'shippingAddressId', 'firstName', 'lastName', 'phoneNumber', 'address' ]);
          if (status === 'SUCCESS') {
            resolve({
              status: true,
              shippingAddress,
            });
          } else {
            resolve({
              status: false,
            });
            logTrack?.({
              params: {
                message: `Submit shipping address failed: ${message}`,
              },
            });
          }
        });
      } else {
        resolve({ status: false });
        logTrack?.({
          params: {
            message: 'Shipping address render failed and can not submit',
          },
        });
      }
    });
  }, [ submitTrigger.current, logTrack, shouldRenderWidget, renderShippingSuccess ]);

  const getShippingAddress = useCallback(() => {
    dispatch(setLatestBillingContact(undefined));
    getAddressTrigger.current().then(({ status, values }) => {
      const { email } = billingContact;
      if (status === 'SUCCESS') {
        const shippingAddress = pick(values, [ 'firstName', 'lastName', 'address' ]);
        dispatch(setLatestBillingContact({ ...shippingAddress, email }));
      } else {
        dispatch(setLatestBillingContact({ ...billingContact, email }));
      }
    });
  }, [ getAddressTrigger.current, billingContact ]);

  useEffect(() => {
    pubSubInstance.subscribe(`${widgetNameSpace}-validate-block`, handleValidate);
    return () => {
      pubSubInstance.unsubscribe(`${widgetNameSpace}-validate-block`, handleValidate);
    };
  }, [ handleValidate, widgetNameSpace ]);

  useEffect(() => {
    pubSubInstance.subscribe(`${widgetNameSpace}-get-shipping-address`, getShippingAddress);
    return () => {
      pubSubInstance.unsubscribe(`${widgetNameSpace}-get-shipping-address`, getShippingAddress);
    };
  }, [ getShippingAddress, widgetNameSpace ]);

  useEffect(() => {
    pubSubInstance.subscribe(`${widgetNameSpace}-submit-block`, handleSubmit);
    return () => {
      pubSubInstance.unsubscribe(`${widgetNameSpace}-submit-block`, handleSubmit);
    };
  }, [ handleSubmit, widgetNameSpace ]);

  return (
    <div id={shippingSectionId} className='cui-mg-t-60'/>
  );
};

export default memo(ShippingAddress);
